import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Modal } from '../../components/ui/Modal.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { productionApi } from '../../services/productionApi.js';

import { BomExplodeModal } from './BomExplodeModal.jsx';

function BomCreateModal({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', productId: '', outputQuantity: '1' });
  const [lines, setLines] = useState([{ componentProductId: '', quantity: '1' }]);

  /**
   * Die Artikelauswahl.
   *
   * Hier stand `limit: 500` gegen ein Schema, das bei 200 abriegelt. Die
   * Antwort war eine 422, die Oberflaeche machte daraus eine leere Liste -
   * und damit war das Anlegen einer Stueckliste komplett blockiert, ohne dass
   * irgendwo eine Fehlermeldung stand. Eine ausgefallene Abfrage sah aus wie
   * "keine Artikel vorhanden".
   *
   * Zwei Aenderungen: die erlaubte Grenze, und eine Suche. Alles auf einmal zu
   * laden geht bei einem Sortiment ueber 200 Artikel ohnehin nicht auf; wer
   * sucht, findet auch den 900sten.
   */
  const [suche, setSuche] = useState('');
  const products = useQuery({
    queryKey: ['inventory', 'products', 'for-bom', suche],
    queryFn: () =>
      inventoryApi.listProducts({ limit: 200, ...(suche.trim() ? { q: suche.trim() } : {}) }),
    enabled: open,
  });
  const productList = products.data?.items || [];
  const gesamt = products.data?.meta?.total ?? productList.length;
  /* Mehr Artikel als geladen: die Auswahl ist dann unvollstaendig, und das
     muss dastehen statt es den Benutzer suchen zu lassen. */
  const abgeschnitten = gesamt > productList.length;
  const productById = (id) => productList.find((p) => p.id === id);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setLine = (i, key) => (e) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [key]: e.target.value } : l)));
  const addLine = () => setLines((ls) => [...ls, { componentProductId: '', quantity: '1' }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const submit = (e) => {
    e.preventDefault();
    const finished = productById(form.productId);
    const cleanLines = lines
      .filter((l) => l.componentProductId && Number(l.quantity) > 0)
      .map((l) => {
        const p = productById(l.componentProductId);
        return {
          componentProductId: l.componentProductId,
          componentSku: p?.sku || null,
          componentName: p?.name || null,
          quantity: l.quantity,
        };
      });
    onCreate({
      name: form.name,
      productId: form.productId,
      productSku: finished?.sku || null,
      productName: finished?.name || null,
      outputQuantity: form.outputQuantity || 1,
      lines: cleanLines,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t('production.boms.new')}>
      <form onSubmit={submit} className="form-stack">
        <div className="field">
          <label className="field-label" htmlFor="bom-name">{t('production.boms.name')}</label>
          <input id="bom-name" className="input" required value={form.name} onChange={set('name')} />
        </div>
        {/* Ein Fehlschlag der Abfrage MUSS sichtbar sein. Vorher sah er aus
            wie ein leeres Sortiment. */}
        {products.isError && (
          <div className="alert danger">
            {t('production.boms.productsFailed', { message: products.error?.message || '' })}
          </div>
        )}
        {!products.isLoading && !products.isError && productList.length === 0 && (
          <div className="alert warning">{t('production.boms.noProducts')}</div>
        )}

        <div className="field">
          <label className="field-label" htmlFor="bom-search">{t('production.boms.search')}</label>
          <input
            id="bom-search"
            className="input"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={t('production.boms.searchHint')}
          />
          {abgeschnitten && (
            <span className="muted text-sm">
              {t('production.boms.truncated', { shown: productList.length, total: gesamt })}
            </span>
          )}
        </div>

        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="bom-product">{t('production.boms.product')}</label>
            <select id="bom-product" className="select" required value={form.productId} onChange={set('productId')}>
              <option value="">{t('common.select')}</option>
              {productList.map((p) => (
                <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="bom-output">{t('production.boms.outputQuantity')}</label>
            <input id="bom-output" type="number" min="0.0001" step="any" className="input" value={form.outputQuantity} onChange={set('outputQuantity')} />
          </div>
        </div>

        <div className="field">
          <label className="field-label">{t('production.boms.components')}</label>
          {lines.map((line, i) => (
            <div className="field-row" key={i} style={{ marginBottom: 'var(--space-2)' }}>
              <div className="field" style={{ flex: 2 }}>
                <select className="select" value={line.componentProductId} onChange={setLine(i, 'componentProductId')}>
                  <option value="">{t('common.select')}</option>
                  {productList.map((p) => (
                    <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <input type="number" min="0.0001" step="any" className="input" value={line.quantity} onChange={setLine(i, 'quantity')} placeholder={t('production.boms.quantity')} />
              </div>
              <button type="button" className="btn" onClick={() => removeLine(i)} disabled={lines.length === 1}>×</button>
            </div>
          ))}
          <button type="button" className="btn" onClick={addLine}>+ {t('production.boms.addComponent')}</button>
        </div>

        <div className="page-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn primary">{t('common.create')}</button>
        </div>
      </form>
    </Modal>
  );
}

export function BomsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [explodeId, setExplodeId] = useState(null);

  const boms = useQuery({
    queryKey: ['production', 'boms'],
    queryFn: () => productionApi.listBoms().then((r) => r.items),
  });

  const createMutation = useMutation({
    mutationFn: (body) => productionApi.createBom(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production', 'boms'] });
      setModalOpen(false);
    },
  });

  const rows = boms.data || [];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('production.boms.title')}</h1>
          <p className="muted">{t('production.boms.subtitle')}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" onClick={() => setModalOpen(true)}>
            {t('production.boms.new')}
          </button>
        </div>
      </header>

      {boms.isLoading ? (
        <Skeleton variant="rows" count={5} />
      ) : rows.length === 0 ? (
        <EmptyState message={t('production.boms.empty')} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('production.boms.code')}</th>
              <th>{t('production.boms.name')}</th>
              <th>{t('production.boms.product')}</th>
              <th className="num">{t('production.boms.outputQuantity')}</th>
              <th className="num">{t('production.boms.components')}</th>
              <th>{t('common.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id}>
                <td className="mono">{b.code}</td>
                <td>{b.name}</td>
                <td>{b.productName || b.productSku || '-'}</td>
                <td className="num">{b.outputQuantity}</td>
                <td className="num">{b.lines?.length ?? 0}</td>
                <td>
                  <span className={`status-pill status-${b.isActive ? 'active' : 'inactive'}`}>
                    {b.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
                <td className="num">
                  <button type="button" className="btn ghost" onClick={() => setExplodeId(b.id)}>{t('production.boms.explode')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <BomCreateModal open={modalOpen} onClose={() => setModalOpen(false)} onCreate={(body) => createMutation.mutate(body)} />
      <BomExplodeModal bomId={explodeId} onClose={() => setExplodeId(null)} />
    </section>
  );
}
